import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    signal,
    SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { EditorView } from '@codemirror/view';
import { beautifyCode, CodeMirrorLanguage } from './code-mirror.util';

/** The lazily-imported `code-mirror.setup` module. */
type CodeMirrorSetupModule = typeof import('./code-mirror.setup');

/**
 * The account app's only code editor: a CodeMirror 6 `EditorView` behind an Angular
 * `ControlValueAccessor`. It replaces both Ace (`ft-code-field-ng`) and Monaco
 * (`ft-code-editor`, and the html source toggle in `ft-text-html-editor`).
 *
 * 🚨 CodeMirror is reached through ONE dynamic `import('./code-mirror.setup')`, deliberately. This
 * component is declared in `EditorsModule`, which is eager, so a static import would put the whole
 * editor in `main.js` — exactly the cost that made the Ace situation worth fixing (Ace was eager in
 * `main.js` for every user on every page, whether or not they ever opened an editor). Keeping the
 * import dynamic means nothing here is downloaded until a code editor actually mounts. Do not
 * "simplify" it back to a static import; only the `code-mirror.util` half (the language union and
 * the js-beautify wrapper) is safe to import statically.
 *
 * Two ways in, deliberately:
 *  - `[ngModel]` / `formControlName`, for the forms-driven editors, and
 *  - the imperative `setValue()`, for `ft-code-field-ng`, which mounts one editor per field
 *    against a plain mutable datasource object and never had a form control at all.
 *
 * Programmatic writes never echo back out as `docChange`, so a host that binds its own model is
 * not fighting the editor on every keystroke.
 */
@Component({
    selector: 'ft-code-mirror',
    standalone: true,
    template: '',
    styles: [
        `:host {
            display: block;
            height: 100%;
            min-height: 0;
            overflow: hidden;
        }`
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FtCodeMirrorComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FtCodeMirrorComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
    /** Syntax mode. Drives highlighting, autocompletion, the linter and the beautifier. */
    @Input() language: CodeMirrorLanguage = 'css';
    /** Ace `setUseWrapMode(true)` / Monaco `wordWrap: 'on'` — on by default, as both editors had. */
    @Input() lineWrapping = true;
    @Input() readOnly = false;
    @Input() placeholder = '';
    /** One-way document seed for hosts that do not use a form control. */
    @Input() value: string | null | undefined = null;

    /** Emitted on USER edits only (never for `setValue` / `writeValue`). */
    @Output() docChange = new EventEmitter<string>();
    /**
     * Emitted once the `EditorView` exists — i.e. AFTER the lazy chunk has loaded, so it is the
     * only reliable "the editor is up" signal. Hosts that keep an index-keyed handle, or that need
     * to read `errorCount`, hang off this rather than off `ngAfterViewInit`.
     */
    @Output() ready = new EventEmitter<FtCodeMirrorComponent>();
    /**
     * Focus/blur of the EDITING SURFACE, as outputs — the same contract `RichTextEditorComponent`
     * exposes (`editorFocus` / `editorBlur`).
     *
     * They exist because a host CANNOT get this from the DOM: the editable node is CodeMirror's
     * `.cm-content` descendant, and native focus/blur do not bubble, so a `(focus)` on
     * `<ft-code-mirror>` binds as a host-element listener that never fires. That is exactly the
     * FTK-7509 defect — `<ngx-monaco-editor>` had the same shape (it declared only `init`), so the
     * html source view of `ft-text-html-editor` never reported focus and its Insert-token dropdown
     * silently inserted nothing.
     */
    @Output() editorFocus = new EventEmitter<void>();
    @Output() editorBlur = new EventEmitter<void>();

    /**
     * Live syntax-error count for this editor — the CodeMirror replacement for Ace's
     * `session.on('changeAnnotation')` recount. Read by `ft-code-field-ng` to gate Save.
     */
    readonly errorCount = signal(0);

    private cm: CodeMirrorSetupModule | null = null;
    private view: EditorView | null = null;
    /** Document text handed in before the view existed (input set / `writeValue` pre-mount). */
    private pendingDoc: string | null = null;
    /** Guards the update listener while WE are writing, so external writes do not echo back. */
    private applyingExternalValue = false;
    /** Set on destroy so a view whose lazy chunk lands late is not attached to a dead component. */
    private destroyed = false;
    private onChangeFn: (value: string) => void = () => undefined;
    private onTouchedFn: () => void = () => undefined;

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly zone = inject(NgZone);

    ngAfterViewInit(): void {
        void this.createView();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] && !changes['value'].firstChange) {
            this.setValue(this.value == null ? '' : String(this.value));
        }
        // Language / wrapping / read-only are static in every current usage; rebuild rather than
        // carry a Compartment per option. Angular only calls this when the binding actually changes.
        const rebuild = ['language', 'lineWrapping', 'readOnly', 'placeholder']
            .some(key => changes[key] && !changes[key].firstChange);
        if (rebuild && this.view) {
            void this.rebuildView();
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroyView();
    }

    // ---- ControlValueAccessor ----

    writeValue(value: any): void {
        this.setValue(value == null ? '' : String(value));
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (this.readOnly === isDisabled) {
            return;
        }
        this.readOnly = isDisabled;
        if (this.view) {
            void this.rebuildView();
        }
    }

    // ---- Imperative API ----

    /**
     * Replace the whole document. A no-op when the text already matches — that equality check is
     * what stops a host re-binding its own model from resetting the caret on every keystroke.
     * Safe to call before the lazy chunk lands: the text is held and seeded into the new view.
     */
    setValue(text: string): void {
        if (!this.view) {
            this.pendingDoc = text;
            return;
        }
        if (this.view.state.doc.toString() === text) {
            return;
        }
        this.applyingExternalValue = true;
        try {
            this.view.dispatch({
                changes: { from: 0, to: this.view.state.doc.length, insert: text }
            });
        } finally {
            this.applyingExternalValue = false;
        }
        this.refreshErrorCount();
    }

    getValue(): string {
        return this.view ? this.view.state.doc.toString() : this.pendingDoc ?? '';
    }

    /**
     * Insert at the caret, replacing any selection — the token-insert path that used to be
     * Monaco's `getSelection()` + `executeEdits`. Counts as a user edit, so it emits `docChange`.
     */
    insertText(text: string): void {
        if (!this.view || !this.cm || !text) {
            return;
        }
        this.view.focus();
        this.view.dispatch(this.cm.insertTextTransaction(this.view.state, text));
    }

    /** Manual replacement for Monaco's `formatOnPaste` / `formatOnType`. */
    format(): void {
        const current = this.getValue();
        if (!current) {
            return;
        }
        const formatted = beautifyCode(current, this.language);
        if (formatted === current) {
            return;
        }
        if (!this.view) {
            this.pendingDoc = formatted;
            return;
        }
        this.view.dispatch({ changes: { from: 0, to: this.view.state.doc.length, insert: formatted } });
    }

    focus(): void {
        this.view?.focus();
    }

    // ---- Internals ----

    /**
     * Swap the view out for one built with the current options, carrying the document across.
     *
     * The document is handed over through `pendingDoc` rather than replayed with `setValue()`
     * afterwards, and that is the whole point: `createView()` always awaits (the grammar load is
     * async even when the chunk is already cached), and a `writeValue()` can land in that window —
     * a form disabling the control while a save response patches it is the realistic case. Replaying
     * a doc captured BEFORE the await would then overwrite the newer value, silently, because
     * `setValue()` suppresses the change event. Staging it in `pendingDoc` lets the later write win:
     * it overwrites `pendingDoc`, and `createView()` seeds from whatever is newest.
     */
    private async rebuildView(): Promise<void> {
        this.pendingDoc = this.getValue();
        this.destroyView();
        await this.createView();
    }

    private async createView(): Promise<void> {
        const cm = this.cm ?? (await import('./code-mirror.setup'));
        const languageExtension = await cm.loadLanguageSupport(this.language);
        // The chunk resolves on a microtask, so the component can already be gone (a modal closed
        // while it was in flight). Attaching a view to a detached host would leak it.
        if (this.destroyed) {
            return;
        }
        this.cm = cm;
        // Outside Angular: CodeMirror attaches scroll / pointer / selection listeners that would
        // otherwise run a full change-detection pass each time. Everything the host cares about
        // re-enters the zone explicitly in `onDocChanged`.
        this.zone.runOutsideAngular(() => {
            this.view = new cm.EditorView({
                state: cm.EditorState.create({
                    doc: this.pendingDoc ?? (this.value == null ? '' : String(this.value)),
                    extensions: [
                        ...cm.buildExtensions({
                            language: this.language,
                            languageExtension,
                            lineWrapping: this.lineWrapping,
                            readOnly: this.readOnly,
                            placeholder: this.placeholder,
                            onDocChanged: doc => this.onDocChanged(doc)
                        }),
                        // Focus/blur of `.cm-content`, re-entered into the zone and re-emitted as
                        // component outputs. Returning false leaves the event unhandled for
                        // CodeMirror, which is what keeps its own focus handling intact.
                        cm.EditorView.domEventHandlers({
                            focus: () => {
                                this.zone.run(() => this.editorFocus.emit());
                                return false;
                            },
                            // Mark the control touched on blur, the same point a native input would.
                            blur: () => {
                                this.zone.run(() => {
                                    this.onTouchedFn();
                                    this.editorBlur.emit();
                                });
                                return false;
                            }
                        })
                    ]
                }),
                parent: this.host.nativeElement
            });
        });
        this.pendingDoc = null;
        this.refreshErrorCount();
        // Back inside the zone: `ready` handlers touch host state (error recounts, token-editor
        // handles) that the view must reflect.
        this.zone.run(() => this.ready.emit(this));
    }

    private destroyView(): void {
        this.view?.destroy();
        this.view = null;
    }

    private onDocChanged(doc: string): void {
        this.refreshErrorCount();
        if (this.applyingExternalValue) {
            return;
        }
        this.zone.run(() => {
            this.onChangeFn(doc);
            this.docChange.emit(doc);
        });
    }

    /**
     * Signal write, so it does not need the Angular zone to be observed — every caller either
     * re-enters the zone immediately afterwards (`onDocChanged`) or is already inside it
     * (`setValue`, `createView`). Keep it that way: a caller that neither is would leave the Save
     * gate reading a stale count until something else happened to trigger change detection.
     */
    private refreshErrorCount(): void {
        if (!this.view || !this.cm) {
            return;
        }
        this.errorCount.set(this.cm.countSyntaxErrors(this.view.state, this.language));
    }
}
